import React, { useEffect, useMemo, useState } from 'react'
import { Menu, MenuProps, Layout, Row, Col, Divider, Space, Select, Typography, Image, Button } from 'antd'
import {
  UserOutlined,
  DashboardOutlined,
  SafetyCertificateOutlined,
  FolderAddOutlined,
  ReadOutlined,
  CodeSandboxOutlined,
  BankOutlined,
  RadarChartOutlined,
  ContainerOutlined,
  ExportOutlined,
  StarOutlined,
  UserSwitchOutlined,
  DeleteOutlined,
  HeartOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import logoApp from '@assets/logo.png'
import Breadcrumb from '@components/widgets/Breadcrumb'
import UserDropdownAuth from '@src/components/widgets/UserDropdownAuth'
import { ACCESS } from '@src/configs/permission'
import { usePermissionCurrent } from '@src/hooks'
import { checkAuth, getLocalStored, setLocalStored } from '@libs/localStorage'
import { Link, useLocation } from 'react-router-dom'
import { LANGUAGE_CODE, LANGUAGE_DEFAULT } from '@src/configs/const.config'
import { queryClient } from '@src/queries'
import { generateUrlFlagLang } from '@src/libs/translation'
import { useMutationPostCleanCache } from '@queries/hooks'

const { Header, Content, Footer, Sider } = Layout

type MenuItem = Required<MenuProps>['items'][number]

const getItem = (
  label: React.ReactNode,
  key: React.Key,
  keyName: ACCESS,
  permissions: ACCESS[],
  icon?: React.ReactNode,
  children?: MenuItem[],
): MenuItem => {
  if (!permissions.includes(keyName)) return null
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem
}

interface ILayoutApp {
  children: JSX.Element
}

function LayoutApp({ children }: ILayoutApp) {
  const lang = getLocalStored('lang')
  const location = useLocation()
  const token = checkAuth()
  const { permissions } = usePermissionCurrent(token)

  const [collapsed, setCollapsed] = useState(false)
  const [selectedKeyMenu, setSelectedKeyMenu] = useState<string[]>(['dashboard'])

  useEffect(() => {
    const pathSnippets = location.pathname.split('/').filter((i) => i)
    if (pathSnippets && pathSnippets?.length > 0) setSelectedKeyMenu(pathSnippets)
  }, [])

  const onSelectMenu = ({ keyPath }: { keyPath: string[] }) => {
    setSelectedKeyMenu(keyPath)
  }

  const menuItems: MenuItem[] = useMemo(
    () => [
      getItem(<Link to="/">Dashboard</Link>, 'dashboard', ACCESS.GENERAL, permissions, <DashboardOutlined />),
      getItem(
        <Link to="/analytics">Analytics</Link>,
        'analytics',
        ACCESS.LIST_USERS,
        permissions,
        <BarChartOutlined />,
      ),
      getItem('Post', 'postG', ACCESS.LIST_POST, permissions, <ReadOutlined />, [
        getItem(<Link to="/post">List post</Link>, 'post', ACCESS.LIST_POST, permissions),
        getItem(<Link to="/create-post">Create post</Link>, 'create-post', ACCESS.CREATE_POST, permissions),
        getItem(<Link to="/category">Category</Link>, 'category', ACCESS.LIST_TAXONOMY, permissions),
      ]),
      getItem('Game', 'gameG', ACCESS.LIST_CONTEST, permissions, <CodeSandboxOutlined />, [
        getItem(<Link to="/game">List game</Link>, 'game', ACCESS.LIST_CONTEST, permissions),
        getItem(<Link to="/create-game">Create game</Link>, 'create-game', ACCESS.CREATE_CONTEST, permissions),
      ]),
      getItem('Reward', 'rewardG', ACCESS.LIST_REWARD, permissions, <RadarChartOutlined />, [
        getItem(<Link to="/reward">List reward</Link>, 'reward', ACCESS.LIST_REWARD, permissions),
        getItem(<Link to="/create-reward">Create reward</Link>, 'create-reward', ACCESS.CREATE_REWARD, permissions),
        getItem(<Link to="/reward-user">Reward User</Link>, 'reward-user', ACCESS.LIST_REWARD_USER, permissions),
      ]),
      getItem('Special Deal', 'specialDealG', ACCESS.LIST_SPECIAL_DEAL_USER, permissions, <BankOutlined />, [
        getItem(
          <Link to="/special-deal">List special deal</Link>,
          'special-deal',
          ACCESS.LIST_SPECIAL_DEAL_USER,
          permissions,
        ),
        getItem(
          <Link to="/create-special-deal">Create special deal</Link>,
          'create-special-deal',
          ACCESS.CREATE_SPECIAL_DEAL,
          permissions,
        ),
        getItem(
          <Link to="/special-deal-user">Special Deal User</Link>,
          'special-deal-user',
          ACCESS.LIST_SPECIAL_DEAL_USER,
          permissions,
        ),
      ]),
      getItem(<Link to="/rating">App rating</Link>, 'Rating', ACCESS.LIST_APP_RATING, permissions, <StarOutlined />),
      getItem(
        <Link to="/post-rating">Post ratings</Link>,
        'post-rating',
        ACCESS.LIST_POST_RATING,
        permissions,
        <StarOutlined />,
      ),
      getItem('Screen', 'OptionG', ACCESS.LIST_OPTION, permissions, <ExportOutlined />, [
        getItem(<Link to="/option/home">Home</Link>, 'option-home', ACCESS.LIST_OPTION, permissions),
        getItem(
          <Link to="/option/special-deal">Special Deal</Link>,
          'option-special-deal',
          ACCESS.LIST_OPTION,
          permissions,
        ),
        getItem(<Link to="/option/faq">FAQs</Link>, 'option-faq', ACCESS.LIST_OPTION, permissions),
        getItem(<Link to="/option/contact">Contact</Link>, 'option-contact', ACCESS.LIST_OPTION, permissions),
        getItem(
          <Link to="/option/introduction">Introduction</Link>,
          'option-introduction',
          ACCESS.LIST_OPTION,
          permissions,
        ),
        getItem(<Link to="/option/ownership">Ownership</Link>, 'option-ownership', ACCESS.LIST_OPTION, permissions),
        getItem(<Link to="/option/term-policy">Term & Policy</Link>, 'term-policy', ACCESS.LIST_OPTION, permissions),
      ]),
      getItem(
        'Notification Template',
        'notificationTemplateG',
        ACCESS.LIST_NOTIFICATION_TEMPLATE,
        permissions,
        <ContainerOutlined />,
        [
          getItem(
            <Link to="/notification-template">List Notification Template</Link>,
            'notification-template',
            ACCESS.LIST_NOTIFICATION_TEMPLATE,
            permissions,
          ),
          getItem(
            <Link to="/create-notification-template">Create Notification Template</Link>,
            'create-notification-template',
            ACCESS.CREATE_NOTIFICATION_TEMPLATE,
            permissions,
          ),
        ],
      ),
      getItem(<Link to="/media">Media Library</Link>, 'media', ACCESS.LIST_MEDIAS, permissions, <FolderAddOutlined />),
      getItem('User', 'userG', ACCESS.LIST_USERS, permissions, <UserOutlined />, [
        getItem(<Link to="/user">List user</Link>, 'user', ACCESS.LIST_USERS, permissions),
        getItem(<Link to="/create-user">Create user</Link>, 'create-user', ACCESS.CREATE_USER, permissions),
      ]),
      getItem(<Link to="/role">Role</Link>, 'role', ACCESS.LIST_ROLES, permissions, <SafetyCertificateOutlined />),
      getItem(
        <Link to="/employee">Employee</Link>,
        'employee',
        ACCESS.LIST_ACCOUNT,
        permissions,
        <UserSwitchOutlined />,
      ),
      getItem('30th Anniversary Event', 'eventG', ACCESS.LIST_POST, permissions, <HeartOutlined />, [
        getItem(<Link to="/event">List event</Link>, 'event', ACCESS.LIST_POST, permissions),
        getItem(<Link to="/create-event">Create event</Link>, 'create-event', ACCESS.CREATE_POST, permissions),
        // getItem(<Link to="/event-wish">Wish</Link>, 'event-wish', ACCESS.LIST_POST, permissions),
        getItem(<Link to="/event-settings">Settings</Link>, 'event-settings', ACCESS.LIST_OPTION, permissions),
      ]),
    ],
    [permissions],
  )

  const onchangeLanguage = async (value: string) => {
    setLocalStored('lang', value)
    await queryClient.refetchQueries()
    window.location.reload()
  }

  const { mutate: mutatePostCleanCache, isLoading: isLoadingPostCleanCache } = useMutationPostCleanCache()
  const handleCleanCache = () => {
    mutatePostCleanCache()
  }

  return (
    <Layout style={{ minHeight: '100vh', padding: 0 }}>
      <Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          width: '100%',
          paddingLeft: 24,
          paddingRight: 24,
        }}
      >
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <img src={logoApp} alt="logo" width={120} />
            </Col>
            <Col>
              <Space id="language" align="center" size={10}>
                <Button type="primary" danger onClick={handleCleanCache} loading={isLoadingPostCleanCache}>
                  {!isLoadingPostCleanCache && <DeleteOutlined />} Purge caches
                </Button>
                <Select
                  style={{ width: 80 }}
                  getPopupContainer={() => document.getElementById('language') as HTMLElement}
                  defaultValue={lang || LANGUAGE_DEFAULT}
                  onChange={onchangeLanguage}
                >
                  {LANGUAGE_CODE.map((l) => (
                    <Select.Option value={l} key={l}>
                      <Space align="center" size={8}>
                        <Image src={generateUrlFlagLang(l)} preview={false} />
                        <Typography.Text style={{ color: '#333' }}>{l.toLocaleUpperCase()}</Typography.Text>
                      </Space>
                    </Select.Option>
                  ))}
                </Select>
                <UserDropdownAuth />
              </Space>
            </Col>
          </Row>
        </Col>
      </Header>
      <Layout style={{ overflow: 'hidden' }}>
        <Sider width={230} collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)} theme="light">
          <Menu
            mode="inline"
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
            theme="light"
            defaultSelectedKeys={selectedKeyMenu}
            onSelect={onSelectMenu}
          />
        </Sider>
        <Layout style={{ padding: '0 24px 0' }}>
          <Row style={{ margin: '16px 0' }}>
            <Breadcrumb />
          </Row>
          <Divider style={{ margin: 0 }} />
          <Content
            style={{
              paddingTop: 10,
              paddingBottom: 10,
              margin: 0,
              minHeight: 280,
            }}
          >
            <Row>{children}</Row>
          </Content>
          <Footer
            style={{
              paddingTop: 24,
              paddingBottom: 24,
              paddingLeft: 0,
              paddingRight: 0,
              textAlign: 'center',
            }}
          >
            {/* <Row gutter={8} align="middle" justify="center"> */}
            {/*  <Col> */}
            {/*    <span>{'Designed and Developer by '}</span> */}
            {/*  </Col> */}
            {/*  <Col> */}
            {/*    <a href="https://twinger.vn/" target="_blank" rel="noreferrer"> */}
            {/*      <img src={logoTWG} alt="logo" width={90} /> */}
            {/*    </a> */}
            {/*  </Col> */}
            {/* </Row> */}
          </Footer>
        </Layout>
      </Layout>
    </Layout>
  )
}

export default LayoutApp
